/**
 * Stage 3: Audio Generation
 * Generate full course audio using Gemini 2.5 Flash TTS
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { CourseDraft, SentenceAudio } from '../../../types/creator-workflow';
import creatorWorkflowService from '../../../services/creatorWorkflowService';
import ProgressBar from '../ProgressBar';
import VoiceCharacterSelector from '../VoiceCharacterSelector';
import { BilingualSubtitles } from '../BilingualSubtitles';
import './Stage3AudioGeneration.css';

import { resolveMediaUrl } from '../../../utils/mediaUrl';
import { getTranslation } from '../../../utils/getTranslation';

const DEFAULT_INWORLD_VOICES = ['Ashley', 'Dennis', 'Sarah', 'Evan', 'Lauren', 'Marcus'];

interface Stage3AudioGenerationProps {
  draft: CourseDraft;
  onGenerateAudio: (options?: any) => Promise<void>;
  onGenerateSegmentedAudio: (options?: any) => Promise<void>;
  onAdvanceStage: () => Promise<void>;
  onGoBackStage: () => Promise<void>;
  loading: boolean;
}

const Stage3AudioGeneration: React.FC<Stage3AudioGenerationProps> = ({
  draft,
  onGenerateAudio: _onGenerateAudio,
  onGenerateSegmentedAudio,
  onAdvanceStage,
  onGoBackStage,
  loading,
}) => {
  const { t } = useTranslation(['creator', 'common']);
  const draftLang = draft.translation_language || 'zh-TW';
  // 從 localStorage 恢復音頻設置
  const getInitialAudioOptions = () => {
    const backup = localStorage.getItem('stage3_form_backup');
    if (backup) {
      try {
        console.log('✅ 從 localStorage 恢復 Stage 3 音頻設置');
        const parsed = JSON.parse(backup);
        return {
          ...parsed,
          ttsProvider: 'inworld',
          model: 'inworld-tts-1.5-max',
          voiceCharacterId: parsed.voiceCharacterId || 'Ashley',
        };
      } catch (err) {
        console.error('Failed to parse Stage 3 backup:', err);
      }
    }
    return {
      voiceCharacterId: 'Ashley', // Default: Inworld Ashley voice
      model: 'inworld-tts-1.5-max',
      speed: 1.0,
      ttsProvider: 'inworld',
    };
  };

  const [audioOptions, setAudioOptions] = useState(getInitialAudioOptions);
  const [speakers, setSpeakers] = useState<string[]>([]);
  const [speakerVoiceMap, setSpeakerVoiceMap] = useState<Record<string, string>>({});
  const [selectedSubtitleLang, setSelectedSubtitleLang] = useState<'off' | 'en' | 'zh-tw' | 'both'>('off');
  const [equalizing, setEqualizing] = useState(false);

  // Ref for auto-scrolling to progress section and audio player
  const progressSectionRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Check if audio exists (needed for polling logic)
  const hasAudio = draft.audio_url && draft.audio_url.length > 0;

  // Track if we're generating segmented audio (has progress) vs full audio (no progress)
  const [isGeneratingSegmented, setIsGeneratingSegmented] = useState(false);

  // No more polling — progress is now driven directly by the parent component
  // via draft.stage_progress.audio_generation updates after each sentence.

  // 從 draft.sentences[].speaker 直接取得所有角色（Sentence Editing 已確認過）
  useEffect(() => {
    if (!draft.id) return;

    // Prefer sentences[] speaker field — accurate after sentence editing + parsing
    const sentences = (draft.sentences ?? []) as Array<{ speaker?: string | null }>;
    const uniqueSpeakers = Array.from(
      new Set(sentences.map(s => s.speaker?.trim()).filter((s): s is string => !!s))
    );

    const detectedSpeakers = uniqueSpeakers.length > 0 ? uniqueSpeakers : ['Narrator'];
    console.log(`✅ 從 sentences 取得 ${detectedSpeakers.length} 個角色:`, detectedSpeakers);
    setSpeakers(detectedSpeakers);

    // 從 localStorage 恢復角色聲音映射
    const backup = localStorage.getItem(`stage3_speaker_voices_${draft.id}`);
    if (backup) {
      try {
        const savedMap = JSON.parse(backup);
        console.log('✅ 從 localStorage 恢復角色聲音映射:', savedMap);
        setSpeakerVoiceMap(savedMap);
        return;
      } catch {
        // fall through to default
      }
    }

    // 初始化默認聲音映射 based on current provider
    const defaultMap: Record<string, string> = {};
    detectedSpeakers.forEach((speaker, index) => {
      defaultMap[speaker] = DEFAULT_INWORLD_VOICES[index % DEFAULT_INWORLD_VOICES.length];
    });
    setSpeakerVoiceMap(defaultMap);
  }, [draft.id, draft.sentences, audioOptions.ttsProvider]);

  // 每次設置變化時保存
  useEffect(() => {
    localStorage.setItem('stage3_form_backup', JSON.stringify(audioOptions));
  }, [audioOptions]);

  // 保存角色聲音映射
  useEffect(() => {
    if (draft.id && Object.keys(speakerVoiceMap).length > 0) {
      localStorage.setItem(`stage3_speaker_voices_${draft.id}`, JSON.stringify(speakerVoiceMap));
    }
  }, [speakerVoiceMap, draft.id]);

  // Scroll to progress bar as soon as generation starts (isGeneratingSegmented → true),
  // so users who are scrolled down to the audio results section see it immediately.
  useEffect(() => {
    if (isGeneratingSegmented && progressSectionRef.current) {
      progressSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isGeneratingSegmented]);

  const voiceProgress = draft.stage_progress.audio_generation;
  const hasMultipleSpeakers = speakers.length > 0;
  // Show success section only when completed AND not currently regenerating.
  // Hiding it while regenerating keeps the progress bar as the main focus.
  const voiceProductionCompleted = (voiceProgress?.completed || hasAudio) && !isGeneratingSegmented;

  // Get segmented audio data from audio_generation stage (merged audio URLs are now stored here)
  const segmentedAudioData = voiceProgress?.segmented_audio_data;
  const hasSegmentedAudio = !!(segmentedAudioData && voiceProgress?.sentences && voiceProgress.sentences.length > 0);

  const handleEqualizeVolume = async () => {
    if (!hasSegmentedAudio) return;

    setEqualizing(true);
    try {
      await creatorWorkflowService.equalizeAudioVolume(draft.id);
      // Reload draft to get updated audio URLs
      window.location.reload();
    } catch (error) {
      console.error('❌ Failed to equalize audio volume:', error);
      alert('音量均衡化失敗，請稍後再試');
    } finally {
      setEqualizing(false);
    }
  };

  const handleGenerateClick = async () => {
    const optionsWithSpeakers = {
      ...audioOptions,
      ...(hasMultipleSpeakers && { speakerVoiceMap }),
      ttsProvider: 'inworld',
    };

    console.log(`🎙️ 使用 Inworld 逐句生成（${hasMultipleSpeakers ? `${speakers.length} 個角色` : '單一角色'}）`);
    setIsGeneratingSegmented(true);
    await onGenerateSegmentedAudio(optionsWithSpeakers);
    setIsGeneratingSegmented(false);
  };

  return (
    <div className="stage-3-audio-generation">
      <header className="stage-header">
        <h2>{t('creator:workflow.stage3.title')}</h2>
      </header>

      {/* Always show configuration section, whether audio exists or not */}
      <div className="audio-configuration">
        <div className="info-box">
          <h3>{t('creator:workflow.stage3.title')}</h3>
          <p><strong>{t('creator:workflow.stage3.contentLength')}</strong> {draft.generated_text?.length || 0}</p>
          {hasAudio && (
            <p className="success-message" style={{ color: '#059669', fontWeight: 600, marginTop: '0.5rem' }}>
              {t('creator:workflow.stage3.audioAlreadyGenerated')}
            </p>
          )}
          {/* Show error message if last generation failed */}
          {voiceProgress?.error && !voiceProgress.completed && (
            <div className="error-message" style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fca5a5',
              borderRadius: '0.5rem',
              padding: '0.75rem 1rem',
              marginTop: '0.75rem',
              color: '#b91c1c'
            }}>
              <strong>{t('common:failed')}:</strong> {voiceProgress.error}
            </div>
          )}
        </div>

            <>
              {/* Voice Character Selection (Inworld TTS) */}
              <div className="form-section" style={{ marginTop: '1.5rem' }}>
                {hasMultipleSpeakers ? (
                  <>
                    <h3>{t('creator:workflow.voiceCharacter.selectVoiceFor', { speaker: '' })}</h3>
                    <p className="help-text">
                      {t('creator:workflow.stage3.speakersDetected', { count: speakers.length })}
                    </p>
                    {speakers.map((speaker) => (
                      <div key={speaker} style={{ marginBottom: '2rem' }}>
                        <VoiceCharacterSelector
                          speaker={speaker}
                          selectedCharacterId={speakerVoiceMap[speaker] || null}
                          onSelectCharacter={(characterId) => {
                            setSpeakerVoiceMap((prev) => ({
                              ...prev,
                              [speaker]: characterId,
                            }));
                          }}

                        />
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    <h3>{t('creator:workflow.voiceSelector.title')}</h3>
                    <VoiceCharacterSelector
                      selectedCharacterId={audioOptions.voiceCharacterId || null}
                      onSelectCharacter={(characterId) => {
                        setAudioOptions((prev: any) => ({ ...prev, voiceCharacterId: characterId }));
                      }}
                      provider={audioOptions.ttsProvider}
                    />
                  </>
                )}
              </div>
            </>

          <div className="form-section">
            <h3>{t('creator:workflow.stage3.speechSpeed')}</h3>
            <div className="form-row">
              <div className="form-group">
                <label>{t('creator:workflow.stage3.speedLabel', { speed: audioOptions.speed })}</label>
                <input
                  type="range"
                  min="0.5"
                  max="2.0"
                  step="0.1"
                  value={audioOptions.speed}
                  onChange={(e) =>
                    setAudioOptions((prev: any) => ({ ...prev, speed: parseFloat(e.target.value) }))
                  }
                />
                <span className="help-text">{t('creator:workflow.stage3.speedHint')}</span>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              onClick={onGoBackStage}
              className="btn-secondary"
              disabled={loading}
            >
              {t('common:back')}
            </button>
            <button
              onClick={handleGenerateClick}
              className={hasAudio ? "btn-secondary" : "btn-primary"}
              disabled={loading}
            >
              {loading ? t('creator:workflow.stage3.generatingAudio') : (hasAudio ? t('creator:workflow.stage3.regenerateAudio') : t('creator:workflow.stage3.generateAudio'))}
            </button>
          </div>

        {/* Show progress bar when segmented audio generation is in progress */}
        {isGeneratingSegmented && (
          <div ref={progressSectionRef} className="progress-section" style={{ marginTop: '1.5rem' }}>
            <h4>{t('creator:workflow.stage3.generatingProgress')}</h4>
            <ProgressBar
              progressPercent={voiceProgress?.progress_percent ?? 0}
              statusMessage={voiceProgress?.status_message ?? 'Starting...'}
              statusKey={voiceProgress?.status_key}
              statusParams={voiceProgress?.status_params}
              currentItem={voiceProgress?.current_item ?? 0}
              totalItems={voiceProgress?.total_items ?? draft.sentences?.length ?? 0}
            />
          </div>
        )}

        {/* Audio preview section - show if audio was generated (check stage completion) */}
        {voiceProductionCompleted && (
          <div className="audio-results">
            <div className="success-box">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                  <h3>{t('creator:workflow.stage3.audioGeneratedTitle')}</h3>
                  <div className="audio-stats">
                    <p>
                      <strong>{t('creator:workflow.stage3.totalSentences')}</strong>{' '}
                      {voiceProgress?.sentences?.length || 0}
                    </p>
                    <p>
                      <strong>{t('creator:workflow.stage3.processingTime')}</strong>{' '}
                      {voiceProgress?.processing_time_ms ? `${(voiceProgress.processing_time_ms / 1000).toFixed(1)}s` : 'N/A'}
                    </p>
                  </div>
                </div>
                {hasSegmentedAudio && (
                  <button
                    onClick={handleEqualizeVolume}
                    className="btn-secondary"
                    disabled={equalizing || loading}
                    title={t('creator:workflow.stage3.reEqualizeVolumeTitle')}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {equalizing ? t('common:processing') : t('creator:workflow.stage3.reEqualizeVolume')}
                  </button>
                )}
              </div>
            </div>

            {/* Merged Audio & Subtitles */}
            {/* Access merged audio data from segmented_audio_data (primary source) or direct properties (fallback) */}
            {(() => {
              // Get merged audio URLs - prefer segmented_audio_data, fallback to direct properties
              const mergedAudioUrl = segmentedAudioData?.merged_audio_url || (voiceProgress as any)?.merged_audio_url;
              const subtitleUrlEn = segmentedAudioData?.subtitle_url_en || (voiceProgress as any)?.subtitle_url_en;
              const subtitleUrlNative = segmentedAudioData?.subtitle_url_native || (voiceProgress as any)?.subtitle_url_native;
              const mergedDuration = segmentedAudioData?.merged_duration_seconds || (voiceProgress as any)?.merged_duration_seconds || 0;

              if (!mergedAudioUrl && !subtitleUrlEn) return null;

              return (
                <div style={{
                  marginTop: '1.5rem',
                  padding: '1rem',
                  backgroundColor: '#f0f8ff',
                  border: '2px solid #2196F3',
                  borderRadius: '8px',
                }}>
                  <h4 style={{ marginBottom: '1rem', color: '#1976d2', fontSize: '16px', fontWeight: 'bold' }}>
                    {t('creator:workflow.stage3.completeAudioPackage')}
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {mergedAudioUrl && (
                      <div>
                        <p style={{ fontSize: '14px', fontWeight: '600', marginBottom: '0.5rem' }}>
                          {t('creator:workflow.stage3.mergedAudioLabel')}
                        </p>

                        {/* Subtitle Toggle Buttons */}
                        {(subtitleUrlEn || subtitleUrlNative) && (
                          <div style={{ marginBottom: '0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => setSelectedSubtitleLang('off')}
                              style={{
                                padding: '0.4rem 0.8rem',
                                backgroundColor: selectedSubtitleLang === 'off' ? '#9E9E9E' : '#E0E0E0',
                                color: selectedSubtitleLang === 'off' ? 'white' : '#333',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '13px',
                                fontWeight: selectedSubtitleLang === 'off' ? 'bold' : 'normal',
                                cursor: 'pointer',
                              }}
                            >
                              {t('creator:workflow.stage3.subtitleOff')}
                            </button>
                            {subtitleUrlEn && (
                              <button
                                onClick={() => setSelectedSubtitleLang('en')}
                                style={{
                                  padding: '0.4rem 0.8rem',
                                  backgroundColor: selectedSubtitleLang === 'en' ? '#2196F3' : '#E3F2FD',
                                  color: selectedSubtitleLang === 'en' ? 'white' : '#1976D2',
                                  border: 'none',
                                  borderRadius: '4px',
                                  fontSize: '13px',
                                  fontWeight: selectedSubtitleLang === 'en' ? 'bold' : 'normal',
                                  cursor: 'pointer',
                                }}
                              >
                                {t('creator:workflow.stage3.subtitleEnglishBtn')}
                              </button>
                            )}
                            {subtitleUrlNative && (
                              <button
                                onClick={() => setSelectedSubtitleLang('zh-tw')}
                                style={{
                                  padding: '0.4rem 0.8rem',
                                  backgroundColor: selectedSubtitleLang === 'zh-tw' ? '#4CAF50' : '#E8F5E9',
                                  color: selectedSubtitleLang === 'zh-tw' ? 'white' : '#2E7D32',
                                  border: 'none',
                                  borderRadius: '4px',
                                  fontSize: '13px',
                                  fontWeight: selectedSubtitleLang === 'zh-tw' ? 'bold' : 'normal',
                                  cursor: 'pointer',
                                }}
                              >
                                {t('creator:workflow.stage3.subtitleNativeBtn')}
                              </button>
                            )}
                            {subtitleUrlEn && subtitleUrlNative && (
                              <button
                                onClick={() => setSelectedSubtitleLang('both')}
                                style={{
                                  padding: '0.4rem 0.8rem',
                                  backgroundColor: selectedSubtitleLang === 'both' ? '#FF9800' : '#FFF3E0',
                                  color: selectedSubtitleLang === 'both' ? 'white' : '#E65100',
                                  border: 'none',
                                  borderRadius: '4px',
                                  fontSize: '13px',
                                  fontWeight: selectedSubtitleLang === 'both' ? 'bold' : 'normal',
                                  cursor: 'pointer',
                                }}
                              >
                                {t('creator:workflow.stage3.subtitleBilingual')}
                              </button>
                            )}
                          </div>
                        )}

                        {/* Audio Player */}
                        <audio
                          ref={audioRef}
                          controls
                          src={mergedAudioUrl}
                          style={{ width: '100%', marginBottom: '0.5rem' }}
                        >
                          {t('creator:workflow.stage3.audioBrowserUnsupported')}
                        </audio>

                        {/* Custom Bilingual Subtitle Display */}
                        <BilingualSubtitles
                          audioRef={audioRef}
                          subtitleUrlEn={resolveMediaUrl(subtitleUrlEn) || undefined}
                          subtitleUrlNative={resolveMediaUrl(subtitleUrlNative) || undefined}
                          displayMode={selectedSubtitleLang}
                        />

                        {mergedDuration > 0 && (
                          <p style={{ fontSize: '13px', color: '#666', marginBottom: '0.5rem' }}>
                            {t('creator:workflow.stage3.audioDuration', {
                              time: `${Math.floor(mergedDuration / 60)}:${String(Math.floor(mergedDuration % 60)).padStart(2, '0')}`,
                            })}
                          </p>
                        )}

                        <a
                          href={resolveMediaUrl(mergedAudioUrl)}
                          download
                          style={{
                            display: 'inline-block',
                            padding: '0.5rem 1rem',
                            backgroundColor: '#2196F3',
                            color: 'white',
                            borderRadius: '6px',
                            textDecoration: 'none',
                            fontSize: '14px',
                            fontWeight: '600',
                          }}
                        >
                          {t('creator:workflow.stage3.downloadCompleteAudio')}
                        </a>
                      </div>
                    )}

                    {/* Download Subtitle Files */}
                    {(subtitleUrlEn || subtitleUrlNative) && (
                      <div style={{ marginTop: '0.75rem' }}>
                        <p style={{ fontSize: '14px', fontWeight: '600', marginBottom: '0.5rem' }}>
                          {t('creator:workflow.stage3.downloadSubtitles')}
                        </p>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {subtitleUrlEn && (
                            <a
                              href={resolveMediaUrl(subtitleUrlEn)}
                              download
                              style={{
                                display: 'inline-block',
                                padding: '0.5rem 1rem',
                                backgroundColor: '#2196F3',
                                color: 'white',
                                borderRadius: '6px',
                                textDecoration: 'none',
                                fontSize: '14px',
                                fontWeight: '600',
                              }}
                            >
                              {t('creator:workflow.stage3.englishSubtitles')}
                            </a>
                          )}
                          {subtitleUrlNative && (
                            <a
                              href={resolveMediaUrl(subtitleUrlNative)}
                              download
                              style={{
                                display: 'inline-block',
                                padding: '0.5rem 1rem',
                                backgroundColor: '#4CAF50',
                                color: 'white',
                                borderRadius: '6px',
                                textDecoration: 'none',
                                fontSize: '14px',
                                fontWeight: '600',
                              }}
                            >
                              {t('creator:workflow.stage3.chineseSubtitles')}
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Sentence Audio Files */}
            {hasSegmentedAudio && (
              <div className="sentences-section" style={{ marginTop: '1.5rem' }}>
                <h4>{t('creator:workflow.stage3.sentenceAudioFiles', { count: voiceProgress?.sentences?.length || 0 })}</h4>

                {/* Show speaker-voice mapping if available */}
                {segmentedAudioData?.speakerVoiceMap && Object.keys(segmentedAudioData.speakerVoiceMap).length > 0 && (
                  <div className="info-box" style={{ marginBottom: '1rem' }}>
                    <h5>🎭 {t('creator:workflow.stage3.speakerVoiceAssignment')}</h5>
                    <ul style={{ marginTop: '0.5rem', listStyle: 'none', padding: 0 }}>
                      {Object.entries(segmentedAudioData.speakerVoiceMap).map(([speaker, voice]) => (
                        <li key={speaker} style={{ marginBottom: '0.3rem' }}>
                          <strong>{speaker}:</strong> {String(voice)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="sentences-list">
                  {(voiceProgress?.sentences || []).map(
                    (sentence: SentenceAudio, index: number) => {
                      const audioUrl = resolveMediaUrl(sentence.audioUrl);

                      // Get voiceCharacterId from sentence or from speakerVoiceMap
                      const voiceCharacterId = sentence.voiceCharacterId ||
                        (sentence.speaker && segmentedAudioData?.speakerVoiceMap?.[sentence.speaker]) ||
                        'Unknown';

                      return (
                        <div key={index} className="sentence-item">
                          <div className="sentence-header">
                            <span className="sentence-number">#{index + 1}</span>
                            {sentence.speaker && (
                              <span className="sentence-speaker" style={{
                                marginLeft: '0.5rem',
                                padding: '0.2rem 0.5rem',
                                backgroundColor: '#e8f5e9',
                                borderRadius: '4px',
                                fontSize: '0.85em',
                                fontWeight: 'bold'
                              }}>
                                🎭 {sentence.speaker} ({voiceCharacterId})
                              </span>
                            )}
                            <span className="sentence-text" style={{ marginLeft: sentence.speaker ? '0.5rem' : 0 }}>
                              {sentence.text}
                            </span>
                          </div>
                          {/* Translation row */}
                          {(sentence.translation || (sentence as any).translations) && (
                            <div className="sentence-translation" style={{
                              marginTop: '0.25rem',
                              fontSize: '0.9em',
                              color: '#666',
                              paddingLeft: '2rem'
                            }}>
                              {getTranslation((sentence as any).translations, draftLang, sentence.translation || '')}
                            </div>
                          )}
                          <div className="sentence-audio">
                            <audio controls src={audioUrl} className="sentence-player">
                              {t('creator:workflow.stage3.audioBrowserUnsupported')}
                            </audio>
                            {/* Show duration if available */}
                            {sentence.duration && (
                              <span style={{ marginLeft: '0.5rem', fontSize: '0.85em', color: '#888' }}>
                                ({sentence.duration.toFixed(1)}s)
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            )}

            <div className="form-actions" style={{ marginTop: '1.5rem' }}>
              <button
                onClick={handleGenerateClick}
                className="btn-secondary"
                disabled={loading}
              >
                {t('creator:workflow.stage3.regenerateAudio')}
              </button>
              <button
                onClick={onAdvanceStage}
                className="btn-primary"
                disabled={loading}
              >
                {t('creator:workflow.stage3.continueToStage4')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Loading overlay removed - all audio generation now uses segmented mode with progress bar */}
    </div>
  );
};

export default Stage3AudioGeneration;
